import {Container} from '@tenancy.nz/ui';
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import Footer from '../../components/Footer';
import ListingErrorMessage from '../../components/ListingErrorMessage';
import Nameplate from '../../components/Nameplate';
import PropertyFilterForm from '../../components/PropertyFilterForm';
import PropertyItem from '../../components/PropertyItem';
import PropertyList from '../../components/PropertyList';
import {
  env,
  filterProperties,
  getStorageItem,
  jsonDecode,
  jsonEncode,
  prepareAgencyDetails,
  preparePropertyListProps,
  setStorageItem,
} from '../../utils/helpers';

function ListingFeature() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {slug} = useParams();
  const [filters, setFilters] = useState(
    jsonDecode(getStorageItem('filters')) || {}
  );

  const {
    data: {agency, properties} = {agency: {}, properties: []},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(slug, {
    skip: !slug.startsWith('tps'),
  });

  useEffect(() => {
    if (!isNaN(parseInt(slug, 10))) {
      navigate(`/listing/${slug}?${searchParams.toString()}`);
    }
  }, [slug, navigate, searchParams]);

  // Animate and scroll to the last scroll point. Typically occurs when back button is clicked.
  useEffect(() => {
    const interval = setTimeout(() => {
      const scrollPoint = window.history.state.lastScrollPos || 0;
      if (scrollPoint > 0 && !isLoading) {
        window.scrollBy({
          top: scrollPoint,
          behavior: 'smooth',
        });
        window.history.replaceState('lastScrollPos', 0);
      }
    }, 300);
    return () => clearTimeout(interval);
  }, [properties, isLoading]);

  const agencyData = useMemo(() => prepareAgencyDetails(agency), [agency]);

  const filteredProperties = useMemo(() => {
    const prepared = preparePropertyListProps(properties);
    return filterProperties(prepared, filters);
  }, [properties, filters]);

  const handleFilterSubmit = values => {
    setStorageItem('filters', jsonEncode(values));
    setFilters(values);
  };

  const handleFilterClear = () => {
    setStorageItem('filters', jsonEncode({}));
    setFilters({});
  };

  if (isLoading) {
    return <Nameplate skeleton />;
  }

  return (
    <>
      {!isError && agencyData.hasBookMeAccess === true && (
        <>
          <Nameplate
            skeleton={isLoading}
            heading={agencyData.companyName}
            logo={agencyData.logo}
            ctaLabel="Manage your bookings"
            ctaHref={`${env('REACT_APP_TENANT_PORTAL_URL')}/viewings`}
          />
          <Container maxWidth="lg">
            <PropertyFilterForm
              initialValues={filters}
              onClear={handleFilterClear}
              onSubmit={handleFilterSubmit}
              countLabel={`Properties: ${filteredProperties.length}`}
            />
            <PropertyList
              items={filteredProperties}
              loading={isLoading}
              renderItem={({
                id,
                rent,
                mediumphoto,
                dateAvailableDisplay,
                ...rest
              }) => (
                <PropertyItem
                  {...rest}
                  photo={mediumphoto}
                  rentAmount={rent}
                  dateAvailable={dateAvailableDisplay}
                  onView={() => {
                    window.history.pushState(
                      {
                        lastScrollPos:
                          document.documentElement.scrollTop ||
                          document.body.scrollTop,
                      },
                      null
                    );
                    navigate(`/listing/${id}?${searchParams.toString()}`, {});
                  }}
                  skeleton={isLoading}
                />
              )}
            />
          </Container>
        </>
      )}
      {!isLoading && (agencyData.hasBookMeAccess !== true || isError) && (
        <ListingErrorMessage />
      )}
      <Footer />
    </>
  );
}

export default ListingFeature;
